import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetClustersByCampusQuery,
  useGetStudentsByClusterLazyQuery,
} from "../../../../graphql/generated.tsx";
import { useMutation, gql } from "@apollo/client";
import { IoMdArrowRoundBack } from "react-icons/io";
import { MdDelete, MdEdit } from "react-icons/md";
import AdminLayout from "../../Layout/index.jsx";
import NetworkIndicator from "../../../Practice/Components/NetworkIndicator.jsx";
import swal from "sweetalert";
import Loader from "../../../../components/Loader.jsx";
import Pagination from "../../../../components/Pagination/index.jsx";

// 🧩 GraphQL mutation for requestDeletion
const REQUEST_DELETION = gql`
  mutation RequestDeletion(
    $typeOfDeletion: String!
    $payload: JSON!
    $reasonForDeletion: String!
    $requestedBy: String!
    $title: String!
  ) {
    requestDeletion(
      typeOfDeletion: $typeOfDeletion
      payload: $payload
      reasonForDeletion: $reasonForDeletion
      requestedBy: $requestedBy
      title: $title
    ) {
      id
      statusOfApproval
    }
  }
`;

// 🧩 GraphQL mutation for updating cluster name
const UPDATE_CLUSTER_NAME = gql`
  mutation UpdateClusterName($updateClusterNameInput: UpdateClusterNameInput!) {
    updateClusterName(updateClusterNameInput: $updateClusterNameInput) {
      id
      name
    }
  }
`;

// 🧩 GraphQL mutation for recalculating cluster student counts
const RECALCULATE_CLUSTER_COUNTS = gql`
  mutation RecalculateClusterCounts($campusName: String!) {
    recalculateClusterStudentCounts(campusName: $campusName)
  }
`;

const ClusterListing = () => {
  const { campusName } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [IsLoad, setIsLoad] = useState(true);
  const limit = 10;

  useEffect(() => {
    setTimeout(() => setIsLoad(false), 2000);
  }, []);

  const { data, refetch, loading } = useGetClustersByCampusQuery({
    variables: { campusName, searchTerm, page, limit: 200 },
  });

  const [requestDeletion] = useMutation(REQUEST_DELETION);
  const [updateClusterName] = useMutation(UPDATE_CLUSTER_NAME);
  const [fetchStudentsByCluster] = useGetStudentsByClusterLazyQuery();
  const [recalculateClusterCounts, { loading: recalcLoading }] = useMutation(
    RECALCULATE_CLUSTER_COUNTS
  );

  // 🔍 Handle Search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
    refetch({ campusName, searchTerm: e.target.value, page: 1, limit: 200 });
  };

  // 📄 Handle Pagination
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // 🗑 Handle Delete Click (Request Deletion)
  const handleDeleteClick = async (cluster) => {
    // Step 1: Ask user to choose deletion type (custom stacked layout)
    const deleteType = await new Promise((resolve) => {
      // Inject small icon CSS scoped to this modal
      const styleId = "swal-delete-cluster-style";
      if (!document.getElementById(styleId)) {
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent = `
          .swal-delete-cluster .swal-icon { display: none !important; }
          .swal-delete-cluster .swal-title { display: none !important; }
          .swal-delete-cluster .swal-text { display: none !important; }
          .swal-delete-cluster .swal-footer { display: none; }
          .swal-delete-cluster .swal-content { width: 100%; padding: 0 4px; margin-top: 0 !important; }
          .swal-delete-cluster { padding: 32px 28px 26px; width: 820px !important; min-height: 420px !important; position: relative !important; border: 2px solid #2f5c7c !important; }
          .swal-del-btn { background: #e5e7eb !important; color: #0E3D5D !important; transition: background 0.18s, color 0.18s !important; }
          .swal-del-btn:hover { background: #d1d5db !important; color: #0E3D5D !important; }
          .swal-del-close-btn:hover { background: #f3f4f6 !important; }
        `;
        document.head.appendChild(style);
      }

      const content = document.createElement("div");
      content.style.cssText = "display:flex;flex-direction:column;gap:10px;width:100%;";
      content.innerHTML = `
        <div style="position:relative;">
          <button id="swal-del-close" class="swal-del-close-btn" style="
            position:absolute;top:-10px;right:-10px;
            width:30px;height:30px;border-radius:50%;border:none;cursor:pointer;
            background:#f3f4f6;color:#6b7280;font-size:18px;line-height:1;
            display:flex;align-items:center;justify-content:center;font-weight:400;
          ">&#x2715;</button>
        </div>
        <div style="text-align:center;margin-bottom:14px;">
          <div style="
            display:inline-flex;align-items:center;justify-content:center;
            width:56px;height:56px;border-radius:50%;
            background:#e8f0f7;color:#0E3D5D;font-size:28px;font-weight:700;
            margin-bottom:12px;
          ">!</div>
          <div style="font-size:18px;font-weight:700;color:#111827;margin-bottom:4px;">Delete "${cluster.name}"</div>
          <div style="font-size:13px;color:#6b7280;">Choose a deletion option:</div>
        </div>
        <div style="display:flex;flex-direction:row;gap:16px;width:100%;align-items:flex-start;">
          <div style="flex:1;display:flex;flex-direction:column;align-items:center;gap:10px;">
            <button id="swal-del-only" class="swal-del-btn" style="
              width:100%;padding:16px 14px;border-radius:8px;border:none;cursor:pointer;
              background:#e5e7eb;color:#0E3D5D;font-size:14px;font-weight:700;letter-spacing:0.01em;
            ">Clear Submissions</button>
            <ul style="font-size:12.5px;color:#374151;text-align:left;line-height:1.8;padding-left:18px;margin:0;list-style:disc;">
              <li>Removes the cluster from the system</li>
              <li>Clears all assignment submissions</li>
              <li>Student profiles are <strong>kept intact</strong></li>
            </ul>
          </div>
          <div style="flex:1;display:flex;flex-direction:column;align-items:center;gap:10px;">
            <button id="swal-del-all" class="swal-del-btn" style="
              width:100%;padding:16px 14px;border-radius:8px;border:none;cursor:pointer;
              background:#e5e7eb;color:#0E3D5D;font-size:14px;font-weight:700;letter-spacing:0.01em;
            ">Clear Student Profile</button>
            <ul style="font-size:12.5px;color:#374151;text-align:left;line-height:1.8;padding-left:18px;margin:0;list-style:disc;">
              <li>Permanently deletes the cluster</li>
              <li>Removes all student profiles from the database</li>
              <li>This action <strong>cannot be undone</strong></li>
            </ul>
          </div>
        </div>
      `;

      swal({
        buttons: false,
        content,
        className: "swal-delete-cluster",
      });

      content.querySelector("#swal-del-only").onclick = () => { swal.close(); resolve("clusterOnly"); };
      content.querySelector("#swal-del-all").onclick = () => { swal.close(); resolve("clusterAndStudents"); };
      content.querySelector("#swal-del-close").onclick = () => { swal.close(); resolve(null); };
    });

    if (!deleteType) return;

    // Step 2: Ask for reason after option is selected
    const reason = await swal({
      text: `Enter reason for requesting deletion of cluster "${cluster.name}":`,
      content: "input",
      buttons: {
        cancel: "Cancel",
        confirm: {
          text: "Submit",
          closeModal: false,
        },
      },
    });

    if (!reason) {
      swal.close();
      return;
    }

    const user = JSON.parse(localStorage.getItem("user"));
    const name = localStorage.getItem("userdisplayName");
    const userdisplayName = name || user?.displayName || user?.userdisplayName || user?.email || "Unknown User";

    try {
      if (deleteType === "clusterAndStudents") {
        // Fetch all students in this cluster
        const { data: studentsData } = await fetchStudentsByCluster({
          variables: { clusterId: cluster.id, searchTerm: "", page: 1, limit: 10000 },
          fetchPolicy: "network-only",
        });

        const students = studentsData?.getStudentsByCluster || [];

        // Backend expects emails (not IDs) — same pattern as StudentDetails bulk delete
        const studentEmails = students.map((s) => s.email);

        if (studentEmails.length === 0) {
          swal.close();
          await swal({
            title: "No Students",
            text: "No students found in this cluster. Use 'Delete Cluster & Submissions Only' instead.",
            icon: "info",
          });
          return;
        }

        // Step 1: Bulk delete all students in cluster
        const bulkDeletePayload = {
          operationName: "DeleteBulkStudents",
          variables: {
            studentIds: studentEmails,
            campusName,
          },
          query: `
            mutation DeleteBulkStudents($studentIds: [String!]!, $campusName: String!) {
              deleteBulkStudents(studentIds: $studentIds, campusName: $campusName) {
                successCount
                failedCount
                deletedEmails
                failedStudents {
                  email
                  studentId
                  reason
                }
                message
              }
            }
          `,
        };

        await requestDeletion({
          variables: {
            typeOfDeletion: "CLUSTER_WITH_STUDENTS",
            payload: bulkDeletePayload,
            reasonForDeletion: reason,
            requestedBy: userdisplayName,
            title: `Delete ${studentEmails.length} student profiles from "${cluster.name}"`,
          },
        });

        // Step 2: Remove the cluster itself
        const clusterPayload = {
          operationName: "RemoveCluster",
          variables: {
            removeClusterInput: {
              campusName,
              clusterId: cluster.id,
            },
          },
          query: `
            mutation RemoveCluster($removeClusterInput: RemoveClusterInput!) {
              removeCluster(removeClusterInput: $removeClusterInput)
            }
          `,
        };

        await requestDeletion({
          variables: {
            typeOfDeletion: "CLUSTER",
            payload: clusterPayload,
            reasonForDeletion: reason,
            requestedBy: userdisplayName,
            title: `Remove cluster "${cluster.name}"`,
          },
        });
      } else {
        // clusterOnly: Step 1 — remove all submissions in cluster
        const submissionPayload = {
          operationName: "RemoveClusterSubmission",
          variables: {
            campusName,
            clusterId: cluster.id,
          },
          query: `
            query RemoveClusterSubmission($campusName: String!, $clusterId: String!) {
              removeClusterSubmission(campusName: $campusName, clusterId: $clusterId)
            }
          `,
        };

        await requestDeletion({
          variables: {
            typeOfDeletion: "CLUSTER_SUBMISSION",
            payload: submissionPayload,
            reasonForDeletion: reason,
            requestedBy: userdisplayName,
            title: `Clear student submissions from "${cluster.name}"`,
          },
        });

        // clusterOnly: Step 2 — remove the cluster itself
        const clusterPayload = {
          operationName: "RemoveCluster",
          variables: {
            removeClusterInput: {
              campusName,
              clusterId: cluster.id,
            },
          },
          query: `
            mutation RemoveCluster($removeClusterInput: RemoveClusterInput!) {
              removeCluster(removeClusterInput: $removeClusterInput)
            }
          `,
        };

        await requestDeletion({
          variables: {
            typeOfDeletion: "CLUSTER",
            payload: clusterPayload,
            reasonForDeletion: reason,
            requestedBy: userdisplayName,
            title: `Remove cluster "${cluster.name}"`,
          },
        });
      }

      await swal({
        title: "Success",
        text: "Deletion request sent successfully!",
        icon: "success",
        buttons: { confirm: true },
        timer: 1800,
      });

      refetch();
    } catch (err) {
      console.error(err);
      swal({
        title: "Error",
        text: "Failed to send deletion request",
        icon: "error",
        buttons: { cancel: "Close" },
      });
    }
  };

  const handleRecalculateCounts = async () => {
    try {
      await recalculateClusterCounts({
        variables: { campusName },
      });

      await swal({
        title: "Success",
        text: "Student counts recalculated successfully!",
        icon: "success",
        buttons: { confirm: true },
        timer: 1800,
      });

      refetch();
    } catch (err) {
      console.error(err);
      swal({
        title: "Error",
        text: "Failed to recalculate student counts",
        icon: "error",
        buttons: { cancel: "Close" },
      });
    }
  };

  // ✏️ Handle Edit Cluster Name
  const handleEditClusterName = async (cluster) => {
    const newName = await swal({
      text: `Enter a new name for cluster "${cluster.name}":`,
      content: "input",
      buttons: {
        cancel: "Cancel",
        confirm: {
          text: "Update",
          closeModal: false,
        },
      },
    });

    if (!newName || newName.trim() === "") {
      swal.close();
      return;
    }

    try {
      await updateClusterName({
        variables: {
          updateClusterNameInput: {
            campusName,
            clusterId: cluster.id,
            newName,
          },
        },
      });

      await swal({
        title: "Success",
        text: "Cluster name updated successfully!",
        icon: "success",
        buttons: { confirm: true },
        timer: 1800,
      });

      refetch();
    } catch (err) {
      console.error(err);
      swal({
        title: "Error",
        text: "Failed to update cluster name",
        icon: "error",
        buttons: { cancel: "Close" },
      });
    }
  };

  const clusters = data?.getClustersByCampus || [];
  const totalPages = Math.ceil(clusters.length / limit);
  const paginatedClusters = clusters.slice((page - 1) * limit, page * limit);

  return (
    <AdminLayout>
      <NetworkIndicator />
      <div className="p-10">

        {/* Header Section */}
        <div className="flex items-center justify-between mb-8 max-md:gap-2">
          <div className="flex items-center">
            <button
              onClick={() => window.history.back()}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <IoMdArrowRoundBack className="w-6 h-6" />
            </button>
            <div className="ml-2 text-2xl font-bold max-md:text-lg">
              {campusName} Clusters
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="Search by name"
              value={searchTerm}
              onChange={handleSearch}
              className="p-2 border border-gray-300 rounded-md"
            />
            <button
              type="button"
              onClick={handleRecalculateCounts}
              disabled={recalcLoading}
              className="px-4 py-2 text-sm font-semibold text-white rounded-md bg-[#0e3d5d] hover:bg-[#0a3048] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {recalcLoading ? "Updating..." : "Refresh Count"}
            </button>
          </div>
        </div>

        {/* Cluster List */}
        {loading ? (
          <p className="text-center text-gray-500"></p>
        ) : clusters.length === 0 ? (
          <p className="text-center text-gray-500">No clusters found.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-6 mt-10 sm:grid-cols-2 lg:grid-cols-3">
              {paginatedClusters.map((cluster) => (
                <div
                  key={cluster.id}
                  onClick={() => navigate(`${cluster.id}`)}
                  className="relative group bg-white shadow-md rounded-lg p-6 hover:shadow-lg flex justify-between items-start cursor-pointer transition-transform duration-200 hover:scale-[1.02]"
                >
                  {/* Cluster Info */}
                  <div className="flex-1">
                    <h3 className="mb-2 text-xl font-semibold">
                      {cluster.name}
                    </h3>
                    <p className="text-gray-700">
                      Students: {cluster.studentCount}
                    </p>
                  </div>

                  {/* 🖊️ Edit + 🗑️ Delete icons on same line */}
                  <div className="flex items-center gap-3">
                    <MdEdit
                      className="text-blue-500 cursor-pointer hover:text-blue-700"
                      size={20}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClusterName(cluster);
                      }}
                      title="Edit Cluster Name"
                    />
                    <MdDelete
                      className="text-red-500 cursor-pointer hover:text-red-700"
                      size={20}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(cluster);
                      }}
                      title="Request Deletion"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center mt-8">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default ClusterListing;
